import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SignIn, User, Lock, Lightning } from '@phosphor-icons/react';

const Login = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate('/mi-dia');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(identifier, password);
    if (result.success) {
      navigate('/mi-dia');
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F172A] px-4 relative overflow-hidden">
      {/* Background gradient orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="w-full max-w-md relative z-10 fade-in-up">
        {/* Logo/Title */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-14 h-14 rounded-[16px] bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-[0_0_30px_rgba(59,130,246,0.4)]">
              <Lightning size={28} weight="fill" className="text-white" />
            </div>
            <h1 className="font-heading text-5xl font-black tracking-tighter text-white">
              SACH<span className="text-blue-500">.</span>
            </h1>
          </div>
          <p className="text-sm text-slate-400 uppercase tracking-[0.25em] font-bold">
            CRM · Cierra más ventas
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-[#111827] border border-white/5 p-8 rounded-[24px] shadow-2xl backdrop-blur-xl">
          <div className="mb-7">
            <h2 className="font-heading text-2xl font-black tracking-tight text-white mb-1">
              Bienvenido
            </h2>
            <p className="text-sm text-slate-400">
              Accede para gestionar tus conversaciones
            </p>
          </div>

          {error && (
            <div
              className="mb-5 p-3 bg-red-500/10 border border-red-500/30 rounded-[12px] flex items-start gap-2"
              data-testid="login-error"
            >
              <p className="text-sm text-red-400 font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="login-form" className="space-y-5">
            <div>
              <label htmlFor="identifier" className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-500 mb-2 block">
                Nombre
              </label>
              <div className="relative">
                <User
                  size={18}
                  weight="bold"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"
                />
                <input
                  id="identifier"
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  placeholder="Tu nombre"
                  className="w-full pl-11 pr-4 py-3 bg-white/[0.03] border border-white/10 rounded-[12px] text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50 focus:bg-white/[0.05] transition-all font-medium"
                  data-testid="identifier-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-500 mb-2 block">
                Contraseña
              </label>
              <div className="relative">
                <Lock
                  size={18}
                  weight="bold"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"
                />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full pl-11 pr-4 py-3 bg-white/[0.03] border border-white/10 rounded-[12px] text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50 focus:bg-white/[0.05] transition-all font-medium"
                  data-testid="password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-bold rounded-[12px] py-3.5 transition-all duration-200 shadow-[0_4px_14px_0_rgba(59,130,246,0.4)] hover:shadow-[0_6px_20px_rgba(59,130,246,0.5)] hover:-translate-y-0.5 disabled:translate-y-0 flex items-center justify-center gap-2"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Iniciando sesión...
                </>
              ) : (
                <>
                  <SignIn size={18} weight="bold" />
                  Iniciar Sesión
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-slate-500" data-testid="register-disabled-notice">
              Acceso restringido. Solicita una cuenta al administrador.
            </p>
          </div>
        </div>

        {/* Footer with legal links */}
        <div className="mt-8 text-center text-xs text-slate-600 flex justify-center gap-3 flex-wrap" data-testid="legal-footer">
          <Link to="/privacy" className="hover:text-blue-400 transition-colors" data-testid="footer-privacy-link">
            Política de Privacidad
          </Link>
          <span className="text-slate-700">·</span>
          <Link to="/terms" className="hover:text-blue-400 transition-colors" data-testid="footer-terms-link">
            Términos del Servicio
          </Link>
          <span className="text-slate-700">·</span>
          <Link to="/data-deletion" className="hover:text-blue-400 transition-colors" data-testid="footer-data-deletion-link">
            Eliminar mis datos
          </Link>
        </div>
        <p className="mt-3 text-center text-xs text-slate-700" data-testid="copyright-text">
          © {new Date().getFullYear()} Grupo Sach · crm.gruposach502.com
        </p>
      </div>
    </div>
  );
};

export default Login;
